// src/pages/Dashboard.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "components/ui/card";
import { Badge } from "components/ui/badge";
import { PageHeader } from "components/ui/page-header";
import { Progress } from "components/ui/progress";
import { BarChart3, Target, TrendingUp, ExternalLink } from "lucide-react";
import MetricsGrid from "components/dashboard/MetricsGrid";
import ConfidenceChart from "components/dashboard/ConfidenceChart";
import RoadmapOverview from "components/dashboard/RoadmapOverview";
import RecentActivity from "components/dashboard/RecentActivity";
import { useAuth } from "../contexts/AuthContext";
import {
  problemCardsService,
  roadmapItemsService,
  issuesService,
  hypothesesService,
  okrsService
} from "../services/database";


export default function Dashboard() {
  const [problems, setProblems] = useState([]);
  const [roadmapItems, setRoadmapItems] = useState([]);
  const [issues, setIssues] = useState([]);
  const [hypotheses, setHypotheses] = useState([]);
  const [okrs, setOkrs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user, loading } = useAuth();

  // Load user-specific data from Supabase database
  const loadDashboardData = useCallback(async () => {
    // Don't load if still authenticating or no user
    if (loading || !user?.id) {
      if (!loading && !user?.id) {
        // Auth completed but no user - clear data
        setProblems([]);
        setRoadmapItems([]);
        setIssues([]);
        setHypotheses([]);
        setOkrs([]);
        setIsLoading(false);
      }
      return;
    }

    setIsLoading(true);
    try {
      console.log('Dashboard loading data for user:', user.id);

      const [
        problemsData,
        roadmapData,
        issuesData,
        hypothesesData,
        okrsData,
      ] = await Promise.all([
        problemCardsService.list("-updated_at", 20),
        roadmapItemsService.list("-updated_at", 15),
        issuesService.list("-updated_date", 50),
        hypothesesService.list("-updated_at", 10),
        okrsService.list("-updated_at", 5),
      ]);

      console.log('Dashboard data loaded:', {
        problems: problemsData.length,
        roadmapItems: roadmapData.length,
        issues: issuesData.length,
        hypotheses: hypothesesData.length,
        okrs: okrsData.length
      });

      setProblems(problemsData);
      setRoadmapItems(roadmapData);
      setIssues(issuesData);
      setHypotheses(hypothesesData);
      setOkrs(okrsData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      // Set empty arrays on error
      setProblems([]);
      setRoadmapItems([]);
      setIssues([]);
      setHypotheses([]);
      setOkrs([]);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, loading]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Add visibility listener to reload data when user comes back to dashboard
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('🔄 Dashboard: Page became visible, reloading data...');
        loadDashboardData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loadDashboardData]);

  const metrics = useMemo(() => {
    const validatedProblems = problems.filter((p) => p.status === "validated").length;
    const criticalIssues = issues.filter((i) => i.tier === 1).length;
    const activeHypotheses = hypotheses.filter((h) => h.status === "testing").length;
    const avgConfidence =
      problems.length > 0
        ? Math.round(
            problems.reduce((sum, p) => {
              // Handle both JSONB confidence_score format and individual score fields
              let d, f, v;

              if (p?.confidence_score && typeof p.confidence_score === 'object') {
                // Database format: JSONB field
                d = Number(p.confidence_score.desirability || 0);
                f = Number(p.confidence_score.feasibility || 0);
                v = Number(p.confidence_score.viability || 0);
              } else {
                // Legacy format: separate fields
                d = Number(p.desirability_score || 0);
                f = Number(p.feasibility_score || 0);
                v = Number(p.viability_score || 0);
              }

              return sum + (d + f + v) / 3;
            }, 0) / problems.length
          )
        : 0;

    return {
      validatedProblems,
      criticalIssues,
      activeHypotheses,
      avgConfidence,
      totalProblems: problems.length,
      roadmapItems: roadmapItems.length,
    };
  }, [problems, issues, hypotheses, roadmapItems]);

  // Simple status color helper for the OKR badge
  const badgeStyleFor = (status) => {
    if (status === "active")
      return { background: "var(--success-soft)", color: "var(--success)" }; // green-ish
    if (status === "paused")
      return { background: "var(--danger-soft)", color: "var(--danger-fg)" }; // red-ish
    return {}; // default badge styles
  };

  return (
    <div>
      <PageHeader
        icon={BarChart3}
        eyebrow="Overview"
        title="Portfolio overview"
        description="Strategic overview of product discovery, delivery, and outcomes."
      />

      {/* Metrics Grid */}
      <div style={{ marginBottom: 16 }}>
        <MetricsGrid metrics={metrics} isLoading={isLoading} />
      </div>

      {/* Main Content Grid: 3 columns (2 left, 1 right) like original */}
      <div className="grid cols-3" style={{ gap: 16 }}>
        {/* Left column spans 2 */}
        <div style={{ gridColumn: "span 2" }}>
          <div className="grid" style={{ gap: 16 }}>
            <ConfidenceChart problems={problems} isLoading={isLoading} />
            <RoadmapOverview roadmapItems={roadmapItems} isLoading={isLoading} />
          </div>
        </div>

        {/* Right column */}
        <div className="grid" style={{ gap: 16 }}>
          <RecentActivity
            problems={problems}
            roadmapItems={roadmapItems}
            issues={issues}
            hypotheses={hypotheses}
            isLoading={isLoading}
          />

          {/* OKR Progress */}
          <Card>
            <CardHeader>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <CardTitle style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--fg)" }}>
                  <Target size={20} color="var(--accent-fg)" />
                  OKR Progress
                </CardTitle>
                {okrs.length > 0 && (
                  <Link
                    to="/okrs"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      fontSize: 12,
                      color: "var(--accent-fg)",
                      textDecoration: "none",
                      fontWeight: 500
                    }}
                  >
                    View All <ExternalLink size={12} />
                  </Link>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {okrs.length > 0 ? (
                <div style={{ display: "grid", gap: 12 }}>
                  {okrs.slice(0, 3).map((okr) => {
                    const keyResultsCount = Array.isArray(okr.key_results) ? okr.key_results.length : 0;
                    const progress = okr.progress_percentage || 0;
                    const progressColor = progress >= 80 ? "var(--success)" : progress >= 60 ? "var(--accent)" : progress >= 40 ? "var(--warning)" : "var(--danger)";

                    return (
                      <div
                        key={okr.id}
                        style={{
                          padding: 12,
                          background: "rgba(248,250,252,0.7)",
                          borderRadius: 8,
                          border: "1px solid var(--border)"
                        }}
                      >
                        <div style={{ marginBottom: 8 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              justifyContent: "space-between",
                              marginBottom: 6,
                              gap: 8
                            }}
                          >
                            <h4 style={{
                              margin: 0,
                              fontSize: 14,
                              fontWeight: 600,
                              color: "var(--fg)",
                              lineHeight: 1.3,
                              flex: 1
                            }}>
                              {okr.title}
                            </h4>
                            <Badge className="text-xs" style={{...badgeStyleFor(okr.status), flexShrink: 0}}>
                              {okr.status || "draft"}
                            </Badge>
                          </div>

                          <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 8,
                            fontSize: 12,
                            color: "var(--fg-muted)"
                          }}>
                            <span>{okr.quarter} {okr.year}</span>
                            <span>•</span>
                            <span>{keyResultsCount} key result{keyResultsCount !== 1 ? 's' : ''}</span>
                          </div>
                        </div>

                        <div style={{ marginBottom: 6 }}>
                          <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 4
                          }}>
                            <span style={{ fontSize: 12, fontWeight: 500, color: "var(--fg-secondary)" }}>Progress</span>
                            <span style={{
                              fontSize: 14,
                              fontWeight: 700,
                              color: progressColor,
                              display: "flex",
                              alignItems: "center",
                              gap: 4
                            }}>
                              <TrendingUp size={12} />
                              {progress}%
                            </span>
                          </div>
                          <Progress value={progress} style={{ height: 6 }} />
                        </div>
                      </div>
                    );
                  })}

                  {okrs.length > 3 && (
                    <Link
                      to="/okrs"
                      style={{
                        display: "block",
                        textAlign: "center",
                        padding: 8,
                        fontSize: 12,
                        color: "var(--accent-fg)",
                        textDecoration: "none",
                        fontWeight: 500,
                        border: "1px dashed var(--border)",
                        borderRadius: 6,
                        background: "rgba(248,250,252,0.5)"
                      }}
                    >
                      +{okrs.length - 3} more OKRs
                    </Link>
                  )}
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "24px 0" }}>
                  <Target size={48} color="var(--border-strong)" style={{ marginBottom: 8 }} />
                  <p style={{ margin: 0, color: "var(--fg-muted)", marginBottom: 8 }}>No OKRs defined yet</p>
                  <p style={{ margin: 0, marginBottom: 12, fontSize: 12, color: "var(--fg-subtle)" }}>
                    Set strategic objectives to get started
                  </p>
                  <Link
                    to="/okrs"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 12px",
                      fontSize: 12,
                      color: "var(--accent-fg)",
                      textDecoration: "none",
                      fontWeight: 500,
                      border: "1px solid var(--border)",
                      borderRadius: 6,
                      background: "var(--bg-surface)"
                    }}
                  >
                    <Target size={14} />
                    Create First OKR
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
